/**
 * About Us page content.
 *
 * Every string in this file is transcribed verbatim from the approved
 * "About Us Page (SEO Optimized 2026)" requirement document
 * (MBA_MERN_Solutions/About Us.docx). Do not reword, shorten or extend this
 * copy — update the document first.
 *
 * The `icon` keys point at the shared registry in ./aboutIcons.js, which is
 * built on the Home page's existing icon set — no new artwork was introduced
 * for this page.
 */

export const aboutSeo = {
  title: "About MBA Solutions | ISO Certification & Compliance Consultancy Experts",
  description:
    "Learn about MBA Solutions, a professional management consultancy providing ISO certification, food safety, sustainability and compliance solutions to businesses worldwide.",
};

export const introContent = {
  title: "About MBA Solutions",
  subtitle:
    "Empowering Organizations Through Certification, Compliance & Sustainable Growth",
  paragraphs: [
    "MBA Solutions is a professional management consultancy dedicated to helping organizations improve their business systems, achieve international certifications and meet global compliance requirements.",
    "We support businesses through every stage of their certification journey — from initial consultation and gap assessment to documentation, implementation, training and certification audit support.",
    "Our goal is to help organizations build effective management systems that improve quality, safety, efficiency and long-term business performance.",
  ],
};

export const storyContent = {
  title: "Our Story",
  subtitle: "Building Excellence Through Global Standards",
  paragraphs: [
    "MBA Solutions was established with the vision of helping businesses adopt international best practices and create systems that support continuous improvement.",
    "Over the years, we have worked with organizations across multiple industries, supporting them in implementing management systems and achieving globally recognized certifications.",
    "Our consultancy approach combines technical knowledge, industry experience and practical business understanding to deliver solutions that create measurable value.",
    "We do not only help businesses achieve certificates — we help them develop systems that continue improving their operations.",
  ],
};

export const visionContent = {
  title: "Our Vision",
  subtitle:
    "Becoming a Trusted Global Partner for Certification & Compliance Solutions",
  paragraphs: [
    "Our vision is to become a trusted regional and international partner for organizations seeking certification, compliance and sustainability solutions.",
    "We aim to support businesses in identifying improvement opportunities, reducing risks and building stronger systems that contribute to sustainable growth.",
  ],
};

export const missionContent = {
  title: "Our Mission",
  subtitle: "Delivering Practical Solutions That Create Business Value",
  intro:
    "Our mission is to provide professional consultancy, training and implementation support that enables organizations to:",
  outcomes: [
    "Improve operational performance",
    "Strengthen quality management systems",
    "Ensure regulatory compliance",
    "Reduce business risks",
    "Achieve international certifications",
    "Build sustainable business practices",
  ],
};

export const valuesContent = {
  title: "Our Core Values",
  values: [
    {
      icon: "integrity",
      title: "Integrity",
      description:
        "We provide honest, ethical and independent consultancy services focused on delivering real business improvement.",
    },
    {
      icon: "excellence",
      title: "Excellence",
      description:
        "We continuously improve our knowledge, methods and services to deliver high-quality solutions.",
    },
    {
      icon: "partnership",
      title: "Partnership",
      description:
        "We work closely with our clients as a trusted business partner throughout their certification journey.",
    },
    {
      icon: "sustainability",
      title: "Sustainability",
      description:
        "We support organizations in creating responsible systems that protect people, environment and business value.",
    },
  ],
};

export const expertiseContent = {
  title: "Our Expertise",
  subtitle: "Professional Knowledge Across Multiple Industries",
  intro: "MBA Solutions brings expertise in:",
  areas: [
    { icon: "iso", name: "ISO Management Systems", to: "/iso-certification-services" },
    { icon: "foodSafety", name: "Food Safety Standards", to: "/food-safety-services" },
    { icon: "infosec", name: "Information Security", to: "/services/information-security" },
    { icon: "textile", name: "Textile Compliance", to: "/textile-compliance-services" },
    { icon: "sustainability", name: "Sustainability Frameworks", to: "/sustainability-esg-services" },
    { icon: "esg", name: "ESG Requirements", to: "/sustainability-esg-services" },
    { icon: "supplyChain", name: "Supply Chain Certifications", to: "/supply-chain-certification-services" },
    { icon: "audit", name: "Internal Audits & Training", to: "/training" },
  ],
  outro:
    "Our consultants understand industry challenges and provide solutions designed according to each organization’s requirements.",
};

export const industryExperienceContent = {
  title: "Our Industry Experience",
  intro: "We support organizations operating in:",
  industries: [
    { icon: "food", name: "Food & Beverage", to: "/industries-we-serve#food-beverage" },
    { icon: "textile", name: "Textile & Garments", to: "/industries-we-serve#textile-garments" },
    { icon: "it", name: "IT & Software", to: "/industries-we-serve#it-software" },
    { icon: "manufacturing", name: "Manufacturing", to: "/industries-we-serve#manufacturing" },
    { icon: "engineering", name: "Engineering", to: "/industries-we-serve#engineering" },
    { icon: "hospitality", name: "Hospitality & Tourism", to: "/industries-we-serve#hospitality-tourism" },
    { icon: "export", name: "Export Businesses", to: "/industries-we-serve#export-businesses" },
    { icon: "logistics", name: "Supply Chain & Logistics", to: "/industries-we-serve#supply-chain-logistics" },
  ],
};

export const whyChooseContent = {
  title: "Why Businesses Choose MBA Solutions",
  blocks: [
    {
      icon: "support",
      title: "Complete Certification Support",
      intro: "We provide end-to-end support including:",
      items: [
        "Gap Assessment",
        "Documentation Development",
        "Training & Awareness",
        "Implementation Support",
        "Internal Audit Preparation",
        "Certification Audit Support",
      ],
    },
    {
      icon: "industryFocus",
      title: "Industry-Focused Approach",
      description:
        "Every organization has different challenges. We create solutions according to your business size, industry requirements and certification goals.",
    },
    {
      icon: "partnership",
      title: "Long-Term Partnership",
      description:
        "Our relationship does not end after certification. We support organizations in maintaining and improving their management systems.",
    },
  ],
};

export const globalSupportContent = {
  title: "Global Support",
  paragraphs: [
    "MBA Solutions supports businesses with certification and compliance requirements across international markets.",
    "Our expertise helps organizations prepare for global buyers, regulatory expectations and international business opportunities.",
  ],
};

export const finalCtaContent = {
  title: "Ready to Improve Your Business Systems?",
  paragraph:
    "Work with MBA Solutions to achieve certification, strengthen compliance and build a sustainable future.",
  cta: { label: "Contact MBA Solutions Today", to: "/contact" },
};
